import React, { useEffect, useRef, lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import Hero from './components/Hero';
import Services from './components/Services';
import WhyChooseUs from './components/WhyChooseUs';
import MeetTheTeam from './components/MeetTheTeam';
import GoogleReviews from './components/GoogleReviews';
import InstagramFeed from './components/InstagramFeed';
import Blog from './components/Blog';
import { PrizeDrawBanner } from './components/PrizeDraw';
import QuoteForm from './components/QuoteForm';
import FAQ from './components/FAQ';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import SEOHead, { homepageSchema } from './components/SEOHead';
import { seoData } from './data/seoData';

// Lazy-loaded routes (code-split — only loaded when visited)
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const PartnersPage = lazy(() => import('./pages/PartnersPage'));
const GetQuotePage = lazy(() => import('./pages/GetQuotePage'));
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'));
const AdminFeedbackPage = lazy(() => import('./pages/AdminFeedbackPage'));
const AdminSurveysPage = lazy(() => import('./pages/AdminSurveysPage'));
const SurveyPage = lazy(() => import('./pages/SurveyPage'));
// SEO Landing Pages
const HouseRemovalsBrighton = lazy(() => import('./pages/HouseRemovalsBrighton'));
const ManAndVanBrighton = lazy(() => import('./pages/ManAndVanBrighton'));
const OfficeRemovalsBrighton = lazy(() => import('./pages/OfficeRemovalsBrighton'));
const RemovalsHove = lazy(() => import('./pages/RemovalsHove'));
const RemovalsEastSussex = lazy(() => import('./pages/RemovalsEastSussex'));
const PackingMaterialsBrighton = lazy(() => import('./pages/PackingMaterialsBrighton'));
const RemovalsWorthing = lazy(() => import('./pages/RemovalsWorthing'));
const RemovalsLewes = lazy(() => import('./pages/RemovalsLewes'));
const RemovalsShoreham = lazy(() => import('./pages/RemovalsShoreham'));
const RemovalsBurgessHill = lazy(() => import('./pages/RemovalsBurgessHill'));
const RemovalsEastbourne = lazy(() => import('./pages/RemovalsEastbourne'));
const StudentRemovalsBrighton = lazy(() => import('./pages/StudentRemovalsBrighton'));
const PianoRemovalsBrighton = lazy(() => import('./pages/PianoRemovalsBrighton'));
const FurnitureDeliveryBrighton = lazy(() => import('./pages/FurnitureDeliveryBrighton'));
// Blog
const BlogIndex = lazy(() => import('./pages/BlogIndex'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const NotFound = lazy(() => import('./pages/NotFound'));
const WinYourMove = lazy(() => import('./pages/WinYourMove'));
const PrizeDrawTerms = lazy(() => import('./pages/PrizeDrawTerms'));

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="w-8 h-8 border-3 border-[#3B43AF] border-t-transparent rounded-full animate-spin" />
  </div>
);

// Scroll to hash on navigation
const ScrollToHash = () => {
  const location = useLocation();
  
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);
  
  return null;
};

// GA4 page_view on SPA route changes. gtag config in index.html already sends the
// first page_view, so the initial render is skipped to avoid double counting.
const GA4PageViews = () => {
  const location = useLocation();
  const isFirst = useRef(true);

  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title
      });
    }
  }, [location.pathname, location.search]);

  return null;
};

// Home page component with all sections
const HomePage = () => {
  return (
    <>
      <SEOHead 
        title={seoData.home.title}
        description={seoData.home.description}
        canonical={seoData.home.canonical}
        schema={homepageSchema}
      />
      <Hero />
      <PrizeDrawBanner />
      <GoogleReviews />
      <Services />
      <WhyChooseUs />
      <MeetTheTeam />
      <InstagramFeed />
      <QuoteForm />
      <Blog />
      <FAQ />
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToHash />
          <GA4PageViews />
          <Header />
          <main>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/get-quote" element={<GetQuotePage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/partners" element={<PartnersPage />} />
                <Route path="/feedback" element={<FeedbackPage />} />
                <Route path="/admin/feedback" element={<AdminFeedbackPage />} />
                <Route path="/admin/surveys" element={<AdminSurveysPage />} />
                <Route path="/survey/:token" element={<SurveyPage />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                {/* SEO Landing Pages */}
                <Route path="/house-removals-brighton" element={<HouseRemovalsBrighton />} />
                <Route path="/man-and-van-brighton" element={<ManAndVanBrighton />} />
                <Route path="/office-removals-brighton" element={<OfficeRemovalsBrighton />} />
                <Route path="/removals-hove" element={<RemovalsHove />} />
                <Route path="/removals-east-sussex" element={<RemovalsEastSussex />} />
                <Route path="/packing-materials-brighton" element={<PackingMaterialsBrighton />} />
                {/* New SEO Landing Pages */}
                <Route path="/removals-worthing" element={<RemovalsWorthing />} />
                <Route path="/removals-lewes" element={<RemovalsLewes />} />
                <Route path="/removals-shoreham" element={<RemovalsShoreham />} />
                <Route path="/removals-burgess-hill" element={<RemovalsBurgessHill />} />
                <Route path="/removals-eastbourne" element={<RemovalsEastbourne />} />
                <Route path="/student-removals-brighton" element={<StudentRemovalsBrighton />} />
                <Route path="/piano-removals-brighton" element={<PianoRemovalsBrighton />} />
                <Route path="/furniture-delivery-brighton" element={<FurnitureDeliveryBrighton />} />
                {/* Blog */}
                <Route path="/blog" element={<BlogIndex />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                {/* Monthly prize draw */}
                <Route path="/win-your-move" element={<WinYourMove />} />
                <Route path="/prize-draw-terms" element={<PrizeDrawTerms />} />
                {/* 404 */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
          <CookieConsent />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
